<template>
  <main class="main">
    <div class="main__body">
      <div class="main__block">
        <div class="block">
          <div class="block__main">
            <h2 class="section-title">Проверка ответов</h2>

            <div class="top-actions">
              <a href="#" class="back-link" @click.prevent="goBack">← Назад</a>
              <CustomButton
                  style="text-align: right"
                  label="Сохранить и посчитать все ответы"
                  :type="1"
                  @click="countAllAnswers"
              />
            </div>

            <div v-if="questions.length > 0" class="question-wrapper">
              <div class="question-block">
                <!-- Вопрос -->
                <div class="block-section">
                  <strong>Вопрос:</strong>
                  <template v-if="!currentQuestion.isMath">
                    <div class="text-content">{{ safeQuestionText }}</div>
                  </template>
                  <template v-else>
                    <div class="text-content" v-html="renderMath(safeQuestionText)" />
                  </template>
                </div>

                <div v-if="currentQuestion.image" class="question-image">
                  <img :src="currentQuestion.image" alt="Изображение вопроса" />
                </div>

                <!-- TYPE 1: Свободный ответ -->
                <template v-if="currentQuestion.type === 1">
                  <div class="block-section">
                    <strong>Правильный ответ:</strong>
                    <template v-if="!currentQuestion.isMath">
                      <div class="text-content correct">{{ currentQuestion.correctText || '—' }}</div>
                    </template>
                    <template v-else>
                      <div class="text-content correct" v-html="renderMath(currentQuestion.correctText || '—')" />
                    </template>
                  </div>

                  <div class="block-section">
                    <strong>Ответ студента:</strong>
                    <template v-if="!currentQuestion.isMath">
                      <div :class="['text-content', correctnessClass]">{{ currentQuestion.userText || '—' }}</div>
                    </template>
                    <template v-else>
                      <div :class="['text-content', correctnessClass]" v-html="renderMath(currentQuestion.userText || '—')" />
                    </template>
                  </div>
                </template>

                <!-- TYPE 2: Выбор (single/multi) -->
                <template v-else-if="currentQuestion.type === 2">
                  <div class="block-section">
                    <strong>Варианты ответа:</strong>
                    <div class="options">
                      <div
                          v-for="opt in currentQuestion.options || []"
                          :key="opt.id"
                          class="option"
                          :class="{
                          'option--ok': opt.correct && opt.selected,
                          'option--missed': opt.correct && !opt.selected,
                          'option--wrong': !opt.correct && opt.selected
                        }"
                          :aria-label="ariaLabelForOption(opt)"
                      >
                        <div class="option-row">
                          <span class="mark">
                            <span v-if="opt.correct && opt.selected">✅</span>
                            <span v-else-if="opt.correct && !opt.selected">☑️</span>
                            <span v-else-if="!opt.correct && opt.selected">❌</span>
                            <span v-else>▫️</span>
                          </span>

                          <div class="option-text">
                            <span v-if="!currentQuestion.isMath">{{ opt.text }}</span>
                            <span v-else v-html="renderMath(opt.text)" />
                            <img
                                v-if="opt.image"
                                :src="opt.image"
                                alt="Изображение варианта"
                                class="option-image"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- TYPE 3 / 4: Соответствие (экспертный вывод «вообще всё») -->
                <template v-else-if="[3,4].includes(currentQuestion.type)">
                  <div class="block-section">
                    <strong>Соответствия</strong>
                    <span
                        class="chip"
                        :title="currentQuestion.orderMode === 'shuffled'
                               ? 'Правая колонка перемешивалась (тип 3)'
                               : 'Правая колонка фиксированная (тип 4)'"
                    >
                      {{ currentQuestion.orderMode === 'shuffled'
                        ? 'Тип 3 · правая колонка перемешана'
                        : 'Тип 4 · правая колонка фикс.' }}
                    </span>

                    <table class="matching">
                      <thead>
                      <tr>
                        <th>Левая часть</th>
                        <th>Правильный ответ</th>
                        <th>Ответ студента</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr
                          v-for="row in currentQuestion.pairs || []"
                          :key="row.left_id"
                          :class="{
                            'match-ok': row.right_user_id === row.right_correct_id,
                            'match-bad': row.right_user_id && row.right_user_id !== row.right_correct_id
                          }"
                      >
                        <!-- Левая часть -->
                        <td>
                          <div class="cell">
                            <span v-if="!currentQuestion.isMath">{{ row.left_text || '—' }}</span>
                            <span v-else v-html="renderMath(row.left_text || '—')" />
                            <img v-if="row.left_image" :src="row.left_image" class="mini" alt="" />
                          </div>
                        </td>

                        <!-- Эталон правой части -->
                        <td>
                          <div class="cell">
                            <span v-if="!currentQuestion.isMath">{{ row.right_correct_text || '—' }}</span>
                            <span v-else v-html="renderMath(row.right_correct_text || '—')" />
                            <img v-if="row.right_correct_image" :src="row.right_correct_image" class="mini" alt="" />
                          </div>
                        </td>

                        <!-- Ответ студента -->
                        <td>
                          <div class="cell">
                            <template v-if="rightById(row.right_user_id)">
                              <span v-if="!currentQuestion.isMath">{{ rightById(row.right_user_id).text }}</span>
                              <span v-else v-html="renderMath(rightById(row.right_user_id).text)" />
                              <img
                                  v-if="rightById(row.right_user_id).image"
                                  :src="rightById(row.right_user_id).image"
                                  class="mini"
                                  alt=""
                              />
                            </template>
                            <template v-else>—</template>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Весь пул правых вариантов (для эксперта) -->
                  <div class="block-section">
                    <strong>Все правые варианты (эталон):</strong>
                    <div class="right-pool">
                      <div v-for="opt in currentQuestion.rightOptions || []" :key="opt.id" class="right-card">
                        <div class="right-card__id">ID: {{ opt.id }}</div>
                        <div class="right-card__text" v-if="!currentQuestion.isMath">{{ opt.text }}</div>
                        <div class="right-card__text" v-else v-html="renderMath(opt.text)" />
                        <img v-if="opt.image" :src="opt.image" class="right-card__img" alt="" />
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Фоллбэк для неожиданных типов -->
                <template v-else>
                  <div class="block-section">
                    <em>Этот тип вопроса пока не поддержан в просмотре.</em>
                  </div>
                </template>

                <!-- Ручная оценка (общая для всех типов) -->
                <div class="manual-eval">
                  <CustomButton
                      label="Правильный"
                      :type="1"
                      :size="'s'"
                      @click="setCorrectness(true)"
                      :class="{ selected: currentQuestion.isCorrect === true }"
                  />
                  <CustomButton
                      label="Неправильный"
                      :type="2"
                      :size="'s'"
                      @click="setCorrectness(false)"
                      :class="{ selected: currentQuestion.isCorrect === false }"
                  />
                </div>

                <div
                    v-if="currentQuestion.isCorrect !== null && currentQuestion.isCorrect !== undefined"
                    class="result"
                    :class="correctnessClass"
                >
                  {{ currentQuestion.isCorrect ? 'Правильный ответ' : 'Неправильный ответ' }}
                </div>
              </div>

              <div class="navigation">
                <CustomButton label="Назад" :disabled="currentIndex === 0" @click="currentIndex--" />
                <span>Вопрос {{ currentIndex + 1 }} из {{ questions.length }}</span>
                <CustomButton label="Вперёд" :disabled="currentIndex >= questions.length - 1" @click="currentIndex++" />
              </div>
            </div>

            <div v-else-if="loading" class="blank">Загрузка...</div>
            <div v-else class="blank"><p class="blank__text">Нет доступных вопросов</p></div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import katex from 'katex'
import 'katex/dist/katex.min.css'
import CustomButton from '@/components/CustomButton.vue'
import { DiagnosticRepository } from '@/repositories'

export default {
  name: 'QuestionsReview',
  components: { CustomButton },
  data() {
    return {
      questions: [],
      currentIndex: 0,
      loading: true,
    }
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentIndex] || {}
    },
    correctnessClass() {
      const val = this.currentQuestion.isCorrect
      if (val === true) return 'correct'
      if (val === false) return 'incorrect'
      return ''
    },
    safeQuestionText() {
      return this.currentQuestion?.question || ''
    },
  },
  methods: {
    rightTextById(id) {
      if (!id || !this.currentQuestion?.rightOptions) return null
      const found = this.currentQuestion.rightOptions.find(r => r.id === Number(id))
      return found?.text || null
    },
    rightById(id) {
      if (!id || !this.currentQuestion?.rightOptions) return null
      return this.currentQuestion.rightOptions.find(r => r.id === Number(id)) || null
    },
    ariaLabelForOption(opt) {
      if (opt.correct && opt.selected) return 'Верный и выбран'
      if (opt.correct && !opt.selected) return 'Верный, но не выбран'
      if (!opt.correct && opt.selected) return 'Неверный, выбран'
      return 'Неверный, не выбран'
    },

    async countAllAnswers() {
      const studentId = this.$route.params.studentId
      try {
        await DiagnosticRepository.countAnswerCorrectness(studentId)
        alert('Ответы успешно подсчитаны.')
      } catch (e) {
        console.error('Ошибка при подсчете ответов', e)
        alert('Не удалось подсчитать ответы.')
      }
    },

    renderMath(text) {
      try {
        return katex.renderToString(String(text ?? ''), {
          throwOnError: false,
          output: 'htmlAndMathml'
        })
      } catch (e) {
        console.error('Ошибка рендеринга формулы', e)
        return String(text ?? '')
      }
    },

    goBack() {
      this.$router.back()
    },

    async fetchData() {
      this.loading = true
      try {
        const studentId = this.$route.params.studentId
        // Эндпоинт должен возвращать ВСЕ типы (см. PHP-функцию выше)
        const res = await DiagnosticRepository.getStudentResults(studentId)
        this.questions = res.data.questions || []
      } catch (e) {
        console.error('Ошибка загрузки данных', e)
      } finally {
        this.loading = false
      }
    },

    async setCorrectness(value) {
      if (this.currentQuestion.isCorrect === value) return
      try {
        const questionId = this.currentQuestion.id // ID StudentQuestion
        await DiagnosticRepository.setAnswerCorrectness(questionId, value)
        this.questions[this.currentIndex].isCorrect = value
      } catch (e) {
        console.error('Не удалось установить корректность', e)
        alert('Ошибка при сохранении оценки')
      }
    }
  },
  created() {
    this.fetchData()
  }
}
</script>

<style scoped>
.section-title { font-size: 1.5rem; margin-bottom: 1.5rem; }
.back-link { display: inline-block; margin-bottom: 1rem; color: #007bff; text-decoration: none; }
.back-link:hover { text-decoration: underline; }

.question-block { background: #fdfdfd; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 6px rgba(0,0,0,0.05); }
.block-section { margin-bottom: 1.25rem; }

.text-content { background: #f8f9fa; padding: 1rem; border-radius: 6px; margin-top: 0.5rem; }
.correct { border-left: 4px solid #4caf50; }
.incorrect { border-left: 4px solid #f44336; }

.top-actions { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }

.result { font-weight: bold; margin-top: 1rem; display: inline-block; padding: 0.5rem 1rem; border-radius: 6px; }
.result.correct { background-color: #e6f4ea; color: #2e7d32; }
.result.incorrect { background-color: #fdecea; color: #c62828; }

.manual-eval { margin-top: 1rem; display: flex; gap: 1rem; }
.manual-eval .selected { box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.3); border-color: #4caf50 !important; font-weight: bold; }

.navigation { display: flex; justify-content: space-between; align-items: center; margin-top: 2rem; }
.blank { padding: 2rem; text-align: center; }
.blank__text { color: #999; }

.question-image { margin-top: 12px; }
.question-image img { max-width: 100%; border-radius: 6px; border: 1px solid #ccc; }

/* TYPE 2 */
.options { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
.option { border: 1px solid #eee; border-radius: 6px; padding: 8px 10px; background: #fafafa; }
.option-row { display: flex; gap: 10px; align-items: center; }
.option-image { max-width: 160px; border-radius: 4px; border: 1px solid #ccc; margin-left: 8px; }
.mark { width: 22px; text-align: center; }
.option--ok { background: #f0fff0; border-color: #b7e0b7; }
.option--missed { background: #fcfff0; border-color: #e0e0b7; }
.option--wrong { background: #fff6f6; border-color: #e0b7b7; }

/* TYPE 3/4: таблица соответствий */
.matching { width: 100%; border-collapse: collapse; margin-top: 8px; }
.matching th, .matching td { border: 1px solid #ddd; padding: 8px; vertical-align: top; text-align: left; }
.cell { display: flex; align-items: center; gap: 8px; }
.mini { max-width: 64px; border-radius: 4px; border: 1px solid #ccc; }
.match-ok { background: #f6fff6; }
.match-bad { background: #fff6f6; }

/* Статус orderMode */
.chip {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #eef3ff;
  border: 1px solid #d7e1ff;
  color: #3b4b8f;
}

/* Весь пул правых вариантов */
.right-pool {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}
.right-card {
  border: 1px solid #eee;
  border-radius: 6px;
  background: #fafafa;
  padding: 8px;
}
.right-card__id { font-size: 12px; color: #666; margin-bottom: 4px; }
.right-card__text { margin-bottom: 6px; }
.right-card__img { max-width: 100%; border-radius: 4px; border: 1px solid #ccc; }
</style>
